"use client"

import { useMemo } from "react"
import { NestedRowTable } from "../../DataTable/NestedRowTable"
import type { ProjectData, AssetNode, ColumnDefinition, WeightInfo, SchemaField } from "../../../types/project"
import { getAllTowerWeightProperties } from "../../../constants/towerWeightTabProperties"
import { getDefaultUnit, getMeasureType } from "../../../utils/unitUtils"
import { debugLog } from "../../../constants/debug"
import type { TabComponentProps } from "../../../types/tabs"
// interface TowerWeightsTabProps {
//   selectedNode?: AssetNode
//   projectData?: ProjectData
// }


export function TowerWeightsTab({ selectedNode, projectData, menuAction }: TabComponentProps) {
  // Generate column definitions from schema
  const columns = useMemo(() => {
    const weightSchema = projectData?.schemas?.Tower_Weight_Schema
    if (!weightSchema) {
      debugLog("No Tower_Weight_Schema found in projectData")
      return []
    }

    debugLog("Generating columns with schema:", weightSchema)

    const allProperties = getAllTowerWeightProperties(weightSchema)
    // Map the fields in the order defined in TOWER_WEIGHT_PROPERTIES
    return allProperties
      .map((fieldKey) => {
        // Find the schema entry that matches our property key
        const schemaEntries = Object.entries(weightSchema) as [string, SchemaField][]
        const matchingSchema = schemaEntries.find(([key]) => key === fieldKey)

        if (!matchingSchema) {
          debugLog(`No schema found for field: ${fieldKey}`)
          return null
        }

        const [_, field] = matchingSchema

        const defaultUnit = getDefaultUnit(field)

        const column: ColumnDefinition = {
          key: fieldKey,
          field: field.field || fieldKey,
          title: field.title || fieldKey,
          units: defaultUnit,
          measure: getMeasureType(defaultUnit),
          type: field.type,
          sortable: true,
          filterable: true,
          align: field.type === "number" ? ("right" as const) : ("left" as const),
        }

        debugLog("Created column definition:", column)

        return column
      })
      .filter((col): col is ColumnDefinition => col !== null)
  }, [projectData])

  // Process weight data with new structure
  const weightData = useMemo(() => {
    const nodeName = selectedNode?.name || ""
    const nodeType = selectedNode?.type || ""

    debugLog("Processing weight data for:", {
      nodeName,
      nodeType,
      hasProjectData: !!projectData,
    })

    if (!projectData?.towerWeights?.Sections) {
      debugLog("No tower weights data available")
      return []
    }

    // Find matching section and its weight info
    const findMatchingData = () => {
      // If looking for Line data, return the first section's WeightInfo that has Type: 'line'
      if (nodeType === "line") {
        const lineData = projectData.towerWeights.Sections.find(
          (section) => section.WeightInfo.Type.toLowerCase() === "line",
        )
        return lineData?.WeightInfo
      }

      // For sections, find the matching section
      if (nodeType === "section") {
        const sectionData = projectData.towerWeights.Sections.find((section) => section.WeightInfo.Name === nodeName)
        return sectionData?.WeightInfo
      }

      // For towers, find the tower in the appropriate section
      if (nodeType === "tower") {
        const parentSection = selectedNode?.parent?.name
        const sectionData = projectData.towerWeights.Sections.find(
          (section) => section.WeightInfo.Name === parentSection,
        )
        if (sectionData?.WeightInfo.WeightInfo) {
          return sectionData.WeightInfo.WeightInfo.find((tower) => tower.Name === nodeName)
        }
      }

      return undefined
    }

    const matchingData = findMatchingData()
    debugLog("Found matching data:", matchingData)

    if (!matchingData) {
      return []
    }

    // Process the weight info into the table structure
    const processWeightInfo = (weight: WeightInfo, parentId = ""): WeightInfo => {
      const currentId = parentId ? `${parentId}-${weight.Name}` : weight.Name
      return {
        ...weight,
        id: currentId,
        WeightInfo: weight.WeightInfo?.map((child) => processWeightInfo(child, currentId)),
      }
    }

    const processedData = processWeightInfo(matchingData)
    debugLog("Processed data:", processedData)

    return [processedData]
  }, [selectedNode, projectData])

  if (!projectData) {
    return <div>No project data available</div>
  }

  debugLog("Rendering table with:", {
    hasData: weightData.length > 0,
    dataLength: weightData.length,
    columnsLength: columns.length,
  })

  return (
    <div style={{ padding: "16px" }}>
      <NestedRowTable
        data={weightData}
        columns={columns}
        levelKey="id"
        childrenKey="WeightInfo"
        tableName="tower-weights"
        title={`${selectedNode?.name || 'Tower'} Weights`}
        menuAction={menuAction}
      />
    </div>
  )
}
